import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useNotifications } from "@/contexts/NotificationContext";

/**
 * FollowButton — one-click follow/unfollow pill.
 *
 * Steve's spec (Feb 2026): "one click — keep informed of changes and
 * updates for members." So we don't show a popover or alert-window
 * picker any more. The first tap subscribes the user to EVERY alert
 * window (week / 3-day / day-before / day-of / after) so they get a
 * full heads-up stream. A second tap unfollows.
 */
const ALL_MODES = ["week_before", "three_days_before", "day_before", "day_of", "after"];

export default function FollowButton({
  targetType = "event",
  targetId,
  label = "Follow",
  testId = "follow-btn",
}) {
  const { refresh: refreshNotifs } = useNotifications();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Hydrate from /api/follows on first render so the pill reflects
  // the user's current state.
  useEffect(() => {
    if (!targetId) return;
    (async () => {
      try {
        const { data } = await api.get("/follows");
        const mine = (data || []).find(
          (f) => f.target_type === targetType && f.target_id === targetId
        );
        if (mine) setFollowing(true);
      } catch (e) {
        console.warn("[follow] hydrate failed:", e?.message || e);
      }
    })();
  }, [targetType, targetId]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      if (following) {
        await api.delete(
          `/follows?target_type=${encodeURIComponent(targetType)}&target_id=${encodeURIComponent(targetId)}`
        );
        setFollowing(false);
      } else {
        await api.post("/follows", {
          target_type: targetType,
          target_id: targetId,
          alert_modes: ALL_MODES,
        });
        setFollowing(true);
      }
      refreshNotifs && refreshNotifs();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className="bd-chip flex items-center gap-1.5"
      style={
        following
          ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)", whiteSpace: "nowrap", fontSize: "0.72rem", padding: "0.25rem 0.6rem" }
          : { whiteSpace: "nowrap", fontSize: "0.72rem", padding: "0.25rem 0.6rem" }
      }
      data-testid={testId}
      title={err || (following ? "Tap to stop receiving updates" : "Tap to follow & get every update")}
      aria-pressed={following}
    >
      {busy ? (
        <Loader2 size={11} className="animate-spin" />
      ) : following ? (
        <Bell size={11} />
      ) : (
        <BellOff size={11} />
      )}
      <span>{following ? "Following" : label}</span>
    </button>
  );
}
